import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import Accordion from '../../../components/Accordion/Accordion';
import Button from '../../../components/Button/Button';
import Image from '../../../components/Image/Image';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import ListItem from '../../../components/ListItem/ListItem';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Pharmaceutical Solutions | General Genomics"
        description="Explore how General Genomics supports pharmaceutical companies with advanced data solutions. Accelerate drug development, optimize clinical trials, and ensure regulatory compliance with secure, synthetic, and de-identified data."
      />
      <Hero
        helper="Industries &gt; Commercial"
        headline="AI-driven data solutions transforming the pharmaceutical industry"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Revolutionize Pharmaceutical Data Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Unlock the full potential of your pharmaceutical research and
          development with General Genomics&apos; AI-driven data solutions. Our
          expertise in synthetic and de-identified data empowers pharmaceutical
          companies to accelerate drug discovery, optimize clinical trials, and
          achieve compliance with strict regulatory standards ( e.g., HIPAA and
          GDPR). With a focus on security, privacy, and innovation, we deliver
          tools that streamline processes, improve predictive outcomes, and
          transform your approach to drug development. Contact us today to see
          how our cutting-edge solutions can drive success in your projects.
        </p>
        <Image
          path="/images/library/woman.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/doctor-man-review.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">How We Help</p>
        <h2 className="action-on-view action-fade-in">
          Data Solutions That Drive Innovation
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Streamlined Drug Discovery:</strong> Leverage synthetic and
            de-identified data to fast-track research and reduce time-to-market.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Optimized Clinical Trials:</strong> Use AI-driven insights
            to enhance participant selection, trial management, and outcome
            prediction.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Regulatory Compliance:</strong> Ensure adherence to
            regulations with secure, privacy-focused solutions.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Enhanced Data Security:</strong> Protect sensitive
            information with robust privacy protocols and cutting-edge
            encryption technologies.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Tailored Solutions:</strong> Collaborate with our experts to
            create data strategies customized to your unique pharmaceutical
            needs.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Dive deeper into General Genomics’ offerings, from innovative AI
          platforms to data governance, and discover tailored solutions for the
          pharmaceutical industry.
        </p>
        <div className="card-group">
          <Card
            icon="science"
            headline="SYN46™"
            subhead="Discover SYN46™, your solution for innovative synthetic data challenges."
            btnURL="/innovation/syn46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="computer"
            headline="Innovation with Curo46®"
            subhead="Discover the AI platform driving advancements in the pharmaceutical industry."
            btnURL="/innovation/curo46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="gavel"
            headline="Data Governance & Compliance"
            subhead="Understand how we ensure regulatory compliance in pharmaceutical applications."
            btnURL="/our-approach/data-governance-and-compliance"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/office-startup-4.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; pharmaceutical
          solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Explore how our data-driven AI solutions accelerate drug development,
          optimize clinical trials, and ensure regulatory compliance.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What are synthetic and de-identified data, and how do they benefit pharmaceutical companies?"
          >
            Synthetic and de-identified data are artificial datasets that
            maintain privacy while simulating real-world data. They help
            pharmaceutical companies accelerate drug discovery, optimize
            clinical trials, and reduce costs by providing rich, accurate data
            without compromising privacy.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics’ AI-driven technology improve clinical trials?"
          >
            Our AI-driven solutions optimize clinical trials by predicting
            outcomes, improving trial designs, and streamlining participant
            selection. This enhances the efficiency and success rate of clinical
            trials, helping pharmaceutical companies bring drugs to market
            faster.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Is General Genomics compliant with regulatory standards?"
          >
            Yes, we ensure strict compliance with regulatory standards,
            including FDA and GDPR. Our data solutions prioritize both legal
            adherence and the security of sensitive information, offering
            pharmaceutical companies peace of mind when using our services.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can General Genomics’ solutions streamline drug development processes?"
          >
            Our data solutions leverage cutting-edge technology to accelerate
            each stage of drug development—from preclinical research to clinical
            trials—by providing high-quality, secure data that supports faster
            decision-making and reduces development timelines.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
